'use client';

import { type Dispatch, type SetStateAction } from 'react';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material/';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';

import { CampaignStatus } from '~/types';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CAMPAIGN_STATUS: { key: `${CampaignStatus}`; name: string }[] = [
  { key: 'ACTIVE', name: 'Ativa' },
  { key: 'INACTIVE', name: 'Inativa' },
  { key: 'DRAFT', name: 'Rascunho' },
  { key: 'REVIEW', name: 'Em Aprovação' },
  { key: 'DONE', name: 'Concluída' },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 9.8 + ITEM_PADDING_TOP,
    },
  },
};

export function StatusSelect({
  selectedStatus,
  setter,
  multiple,
  id,
  label,
  growAtLg,
  fullWidth,
  maxWidth,
}:
  | {
      selectedStatus: '' | `${CampaignStatus}`;
      setter: Dispatch<SetStateAction<'' | `${CampaignStatus}`>>;
      multiple?: false;
      id?: string;
      label?: string;
      growAtLg?: boolean;
      fullWidth?: boolean;
      maxWidth?: string;
    }
  | {
      selectedStatus: `${CampaignStatus}`[];
      setter: Dispatch<SetStateAction<`${CampaignStatus}`[]>>;
      multiple: true;
      id?: string;
      label?: string;
      growAtLg?: boolean;
      fullWidth?: boolean;
      maxWidth?: string;
    }) {
  const selectedStatusList = Array.isArray(selectedStatus)
    ? selectedStatus
    : [];

  const handleChange = ({
    target,
  }: SelectChangeEvent<`${CampaignStatus}` | `${CampaignStatus}`[]>) => {
    const { value } = target;

    if (multiple) {
      const selectedValues =
        typeof value === 'string'
          ? (value.split(',') as `${CampaignStatus}`[])
          : (value as `${CampaignStatus}`[]);

      (setter as Dispatch<SetStateAction<`${CampaignStatus}`[]>>)(
        selectedValues
      );
      return;
    }

    (setter as Dispatch<SetStateAction<'' | `${CampaignStatus}`>>)(
      value === selectedStatus ? '' : (value as `${CampaignStatus}`)
    );
  };

  const labelSelect = label ?? 'Status';

  return (
    <FormControl
      variant="filled"
      fullWidth={fullWidth}
      sx={theme => ({
        ...(maxWidth && { maxWidth }),
        ...(growAtLg && {
          [theme.breakpoints.down('lg')]: { maxWidth: '100%' },
        }),
      })}
    >
      <InputLabel id="campaign-status-select-label">{labelSelect}</InputLabel>
      <Select
        multiple={multiple}
        label={labelSelect}
        placeholder={labelSelect}
        labelId="campaign-status-select-label"
        id={id ?? 'weekdays-select'}
        onChange={handleChange}
        value={multiple ? selectedStatusList : selectedStatus}
        sx={{ borderRadius: 0 }}
        renderValue={selected => {
          if (Array.isArray(selected)) {
            if (selected.length === 0) return '';

            const selectedNames = CAMPAIGN_STATUS.filter(status =>
              selected.includes(status.key)
            ).map(status => status.name);

            if (selectedNames.length <= 1) {
              return selectedNames[0] ?? '';
            }

            const additionalCount = selectedNames.length - 1;

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedNames[0]}
                </Box>
                <Box
                  component="span"
                  sx={theme => ({
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 999,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.selected,
                    border: `1px solid ${theme.palette.action.focus}`,
                    whiteSpace: 'nowrap',
                  })}
                >
                  +{additionalCount} status
                </Box>
              </Box>
            );
          }

          return (
            CAMPAIGN_STATUS.find(status => status.key === selected)?.name ?? ''
          );
        }}
        MenuProps={MENU_PROPS}
      >
        {!multiple && (
          <MenuItem value={selectedStatus} sx={{ display: 'none' }} />
        )}
        {CAMPAIGN_STATUS.map(status => (
          <MenuItem
            key={status.key}
            value={!multiple && status.key === selectedStatus ? '' : status.key}
          >
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={
                multiple
                  ? selectedStatusList.includes(status.key)
                  : selectedStatus === status.key
              }
            />
            <ListItemText primary={status.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
